'use client';

import { ReactNode, useCallback, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import { usePathname, useRouter } from 'next/navigation';
import { cn } from '@utils/cn';
import { TwoColumnLayout } from '@components/layout/Layouts/TwoColumnLayout';
import { CandidateJobsSidebar } from '@components/features/candidate-jobs-sidebar';
import { CompanyJobsSidebar } from '@components/features/company-jobs-sidebar/CompanyJobsSidebar';
import { SidebarNavigationProvider, useSidebarNavigation } from '@components/contexts/SidebarNavigationContext';
import { FilterDrawerProvider } from '@components/contexts/FilterDrawerContext';
import { MissionControlBestieChatProvider } from '@components/contexts/MissionControlBestieChatContext';
import { McSharedStateProvider, useMcSharedState } from '@components/contexts/McSharedStateContext';
import { CompanyApplicantProvider } from '@components/contexts/CompanyApplicantContext';
import { CandidatePromptBar } from '@components/features/candidate-prompt-bar';
import { useMissionControlContext, type MissionControlContext } from '@hooks/features/useMissionControlContext';
import { writeMcContextCookieClient } from '@utils/mc-context-cookie/mcContextCookie';
import { getI18nLinkHref } from '@services/i18nPaths';
import type { JobSearchQueryParams } from '@type/filters';

// Switch context with a hard reload to /mc (the job-search home). A hard reload is required
// (not soft nav) so the server re-reads the cookie and the shell re-renders for the new context.
// Switching only changes the sidebar — the destination is always the job-search home regardless
// of direction.
const switchContextHardReload = (next: MissionControlContext, locale: Locale) => {
  if (typeof window === 'undefined') return;
  writeMcContextCookieClient(next);
  window.location.assign(getI18nLinkHref('/mc', locale));
};

const googleApiKey = process.env.GOOGLE_MAPS_CLIENT_API_KEY as string;

// Pages that render their OWN bottom prompt, so the shell must not add a second bar:
// /mc/job-add, /mc/company, /mc/cv/search (collapsedFab mission chips) and /mc/my-jobs (its own
// McTalentSearchPrompt — the CV-search bar).
const PAGE_RENDERS_OWN_PROMPT_BAR = /\/mc\/(job-add|company|cv\/search|my-jobs)(\/|$)/;
// Company applicant/flow pages (/mc/job/{slug}/applicants[/...]) render CompanyFloatingNavBar
// (also fixed at the bottom), so the company shell's prompt bar is suppressed there to avoid a
// collision. Narrow to `/applicants` so candidate job-results (/mc/job/list/...) are NOT caught.
const PAGE_RENDERS_COMPANY_FLOATING_NAV = /\/mc\/job\/[^/]+\/applicants/;
// Shell-hosted areas that should show NO prompt bar at all (the wallet area). Everything else
// hosted in the shell (the root `/`, `/mc`, results, …) shows it.
const PAGE_HIDES_PROMPT_BAR = /\/wallet(\/|$)/;
// Standalone job detail page (`/mc/job/[slug]`, ro `/mc/loc-de-munca/[slug]`) — the fixed apply
// button bar owns the bottom of the screen there, so the prompt bar would overlap it. Excludes
// the untranslated list path (`/mc/job/list`) and any sub-pages (applicants, after-apply, …).
const PAGE_IS_JOB_DETAIL = /\/mc\/(?:job|loc-de-munca)\/(?!list(?:\/|$))[^/]+\/?$/;
// Edit pages (profile/CV editor) where the prompt input is not useful: show only the mission
// chips instead of the full bar (same as /mc/company/edit, which renders its own collapsedFab).
const PAGE_PROMPT_BAR_CHIPS_ONLY = /\/mc\/(profile|cv)(\/|$)/;

interface McShellProps {
  children: ReactNode;
  locale: Locale;
  isAuthenticated: boolean;
  /** Server-derived context for the first paint (cookie/wallet), to avoid a candidate↔company flash. */
  initialContext: MissionControlContext;
}

/** Dispatches close events for sidebar + detail modal on mobile. */
const dispatchMobileClose = () => {
  if (typeof window !== 'undefined' && window.innerWidth < 1024) {
    window.dispatchEvent(new Event('close-sidebar'));
    window.dispatchEvent(new Event('close-detail-modal'));
  }
};

// ---------------------------------------------------------------------------
// Candidate shell
// ---------------------------------------------------------------------------
const CandidateShellInner = ({ children, locale, isAuthenticated }: Omit<McShellProps, 'initialContext'>) => {
  const { currentView, navigateToCandidateSearch } = useSidebarNavigation();
  const { submitChat } = useMcSharedState();
  const pathname = usePathname() ?? '';

  const handleSwitchContext = useCallback(() => {
    dispatchMobileClose();
    switchContextHardReload('company', locale);
  }, [locale]);

  const handleOpenMap = useCallback((queryParams?: Partial<JobSearchQueryParams>) => {
    if (!googleApiKey) return;
    void (async () => {
      const { showJobsMapModal } = await import('@components/templates/job/list/JobsMapModal');
      showJobsMapModal({ googleApiKey, locale, queryParams });
    })();
  }, [locale]);

  // Fixed prompt bar on the candidate experiences (root `/`, `/mc`, search, results, coaching).
  // Hidden on the wallet area, the job detail page and where the page owns its own bottom UI.
  const showPromptBar = currentView !== 'my-bestie-landing'
    && !PAGE_HIDES_PROMPT_BAR.test(pathname)
    && !PAGE_IS_JOB_DETAIL.test(pathname)
    && !PAGE_RENDERS_OWN_PROMPT_BAR.test(pathname)
    && !PAGE_RENDERS_COMPANY_FLOATING_NAV.test(pathname);
  const showChipsOnly = showPromptBar && PAGE_PROMPT_BAR_CHIPS_ONLY.test(pathname);

  return (
    <TwoColumnLayout
      leftSidebar={(
        <CandidateJobsSidebar
          locale={locale}
          isAuthenticated={isAuthenticated}
          onSwitchContext={handleSwitchContext}
          onOpenMap={handleOpenMap}
        />
      )}
      mainContent={(
        <div className={cn('flex min-h-full flex-col bg-surface-100', showPromptBar && 'pb-28 lg:pb-24')}>
          {children}
          {showPromptBar && (
            <CandidatePromptBar
              locale={locale}
              isAuthenticated={isAuthenticated}
              onSearchSubmit={navigateToCandidateSearch}
              onChatSubmit={submitChat}
              collapsedFab={showChipsOnly}
            />
          )}
        </div>
      )}
      rightSidebar={undefined}
    />
  );
};

// Candidate providers are hoisted ABOVE the candidate/company split so candidate content
// (e.g. /mc/bestie-chat, which renders JobListContent and requires SidebarNavigationProvider)
// can render inside the company shell too. This makes pages shell-agnostic: mission selection
// navigates without switching context, and a candidate page opened from the company context no
// longer crashes for want of its providers. The state is cheap/local, so always mounting it for
// company users is harmless.
const CandidateProviders = ({ children, locale }: { children: ReactNode; locale: Locale }) => (
  <SidebarNavigationProvider locale={locale}>
    <FilterDrawerProvider>
      <MissionControlBestieChatProvider>
        <McSharedStateProvider locale={locale}>
          {children}
        </McSharedStateProvider>
      </MissionControlBestieChatProvider>
    </FilterDrawerProvider>
  </SidebarNavigationProvider>
);

// ---------------------------------------------------------------------------
// Company shell — one sidebar (CompanyJobsSidebar) for every company route, with
// `selectedJobSlug` derived from the URL (the FlowPageSidebar pattern) and URL-driven
// job/tag navigation. Replaces the old per-page CompanyLayout / FlowPageSidebar split.
// ---------------------------------------------------------------------------
const CompanyShellInner = ({ children, locale, isAuthenticated }: Omit<McShellProps, 'initialContext'>) => {
  const router = useRouter();
  const pathname = usePathname() ?? '';
  const { currentView, navigateToCandidateSearch } = useSidebarNavigation();
  const { submitChat } = useMcSharedState();

  // /mc/job/{slug}/... → highlight that job in the sidebar.
  const jobMatch = pathname.match(/\/mc\/job\/([^/]+)/);
  const selectedJobSlug = jobMatch ? jobMatch[1] : undefined;

  const handleSwitchContext = useCallback(() => {
    dispatchMobileClose();
    switchContextHardReload('candidate', locale);
  }, [locale]);

  // CompactJobCard's links are plain <a> (full reload). These callbacks intercept them and
  // soft-navigate instead — same destinations as the card hrefs (title → applicants/input-flow,
  // NEW → applicants?status=1) but without the persistent shell remounting.
  const handleJobSelect = useCallback((job: ApiSchema<'McMyJobCard'>) => {
    dispatchMobileClose();
    router.push(getI18nLinkHref(`/mc/job/${job.slug}/applicants/input-flow`, locale));
  }, [router, locale]);

  const handleTagClick = useCallback((job: ApiSchema<'McMyJobCard'>, status: number) => {
    dispatchMobileClose();
    const base = getI18nLinkHref(`/mc/job/${job.slug}/applicants`, locale);
    router.push(`${base}?status=${status}&withPlaceholders=true`);
  }, [router, locale]);

  // The shell's bar only ever appears on candidate-experience pages (root `/`, `/mc`, search,
  // results, coaching) — every company-specific page owns its bar or uses CompanyFloatingNavBar.
  // So render the SAME full prompt bar as the candidate shell (mode derived from the view:
  // search on job search, chat on coaching), hidden on the wallet area / own-bar / flow pages.
  const showPromptBar = currentView !== 'my-bestie-landing'
    && !PAGE_HIDES_PROMPT_BAR.test(pathname)
    && !PAGE_IS_JOB_DETAIL.test(pathname)
    && !PAGE_RENDERS_OWN_PROMPT_BAR.test(pathname)
    && !PAGE_RENDERS_COMPANY_FLOATING_NAV.test(pathname);
  const showChipsOnly = showPromptBar && PAGE_PROMPT_BAR_CHIPS_ONLY.test(pathname);

  return (
    <TwoColumnLayout
      leftSidebar={(
        <CompanyJobsSidebar
          locale={locale}
          isAuthenticated={isAuthenticated}
          selectedJobSlug={selectedJobSlug}
          onJobSelect={handleJobSelect}
          onTagClick={handleTagClick}
          onSwitchContext={handleSwitchContext}
        />
      )}
      mainContent={(
        <div className={cn('flex min-h-full flex-col bg-surface-100', showPromptBar && 'pb-28 lg:pb-24')}>
          {children}
          {showPromptBar && (
            <CandidatePromptBar
              locale={locale}
              isAuthenticated={isAuthenticated}
              onSearchSubmit={navigateToCandidateSearch}
              onChatSubmit={submitChat}
              collapsedFab={showChipsOnly}
            />
          )}
        </div>
      )}
      rightSidebar={undefined}
    />
  );
};

const CompanyShell = ({ children, locale, isAuthenticated }: Omit<McShellProps, 'initialContext'>) => (
  <CompanyApplicantProvider>
    <CompanyShellInner locale={locale} isAuthenticated={isAuthenticated}>
      {children}
    </CompanyShellInner>
  </CompanyApplicantProvider>
);

/**
 * @description Persistent Mission Control shell. Mounted once by mc/layout for the whole
 * `/mc/*` subtree so the sidebar + providers + (candidate) chat/prompt survive navigation —
 * only the page content (`children`) changes per route. Renders the candidate or company
 * shell based on the current context (seeded by the server-derived `initialContext`).
 */
export const McShell = ({
  children, locale, isAuthenticated, initialContext,
}: McShellProps) => {
  const { t } = useTranslation('common');
  const { currentContext } = useMissionControlContext({ initialContext });

  useEffect(() => {
    const isCompany = currentContext === 'company';
    document.title = isCompany
      ? t('bestie.hire.meta.title', 'BestJobs Hire – Candidați intervievați, rezultate garantate')
      : t('homepage.react.meta.title', 'BestJobs');
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', isCompany
        ? t('bestie.hire.meta.description', 'Bestie este agentul tău AI de recrutare.')
        : t('meta.homepage.description_react', 'BestJobs – Stop searching, start interviewing!'));
    }
  }, [currentContext, t]);

  return (
    <CandidateProviders locale={locale}>
      {currentContext === 'company'
        ? <CompanyShell locale={locale} isAuthenticated={isAuthenticated}>{children}</CompanyShell>
        : <CandidateShellInner locale={locale} isAuthenticated={isAuthenticated}>{children}</CandidateShellInner>}
    </CandidateProviders>
  );
};
